import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { Mail, CheckCircle2, XCircle, MinusCircle, Send, Inbox, Clock, Hourglass } from "lucide-react";

const CATEGORIES = {
  invite: { label: "Invito erede", cls: "bg-[#EEF3EC] text-[#4b6b47]" },
  gift: { label: "Dono a data", cls: "bg-[#FBEDE7] text-[#b05a3f]" },
  gift_confirmation: { label: "Conferma recapito", cls: "bg-[#EEF3EC] text-[#4b6b47]" },
  release: { label: "Rilascio post-scomparsa", cls: "bg-[#EDE9F3] text-[#5b4b7a]" },
  declaration: { label: "Comunicazione scomparsa", cls: "bg-[#F3ECE4] text-[#7a5b3a]" },
  admin_verification: { label: "Richiesta verifica", cls: "bg-[#E9EEF3] text-[#3a5b7a]" },
  verification_result: { label: "Esito verifica", cls: "bg-[#E9EEF3] text-[#3a5b7a]" },
  other: { label: "Altro", cls: "bg-[#F0E9DC] text-[#5a5148]" },
};

const STATUS = {
  sent: { label: "Inviata", icon: CheckCircle2, cls: "text-[#4b6b47]" },
  failed: { label: "Fallita", icon: XCircle, cls: "text-destructive" },
  skipped: { label: "Non inviata", icon: MinusCircle, cls: "text-muted-foreground" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const tzShort = (tz) => {
  const map = { "Europe/Rome": "ora italiana", "UTC": "UTC" };
  return map[tz] || (tz || "").split("/").pop().replace(/_/g, " ");
};
const fmtDateTz = (iso, tz) => {
  if (!iso) return "—";
  try {
    const opts = { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" };
    if (tz) opts.timeZone = tz;
    const s = new Date(iso).toLocaleString("it-IT", opts);
    return tz ? `${s} (${tzShort(tz)})` : s;
  } catch {
    return iso;
  }
};

const RegistroInvii = () => {
  const [logs, setLogs] = useState(null);
  const [pending, setPending] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/email-log").then((res) => setLogs(res.data)).catch(() => setLogs([]));
    api.get("/scheduled-pending").then((res) => setPending(res.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!logs) return [];
    if (filter === "all") return logs;
    return logs.filter((l) => l.category === filter);
  }, [logs, filter]);

  const usedCategories = useMemo(() => {
    if (!logs) return [];
    return [...new Set(logs.map((l) => l.category))];
  }, [logs]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 py-10 fade-in" data-testid="registro-invii-page">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#FBEDE7] flex items-center justify-center">
            <Mail className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="font-serif text-4xl tracking-tight">Registro invii</h1>
            <p className="text-muted-foreground mt-1">
              Tutte le email inviate dal tuo account Eterna, con data e destinatario, in un unico luogo.
            </p>
          </div>
        </div>

        {pending.length > 0 && (
          <div className="mb-8" data-testid="pending-section">
            <div className="flex items-center gap-2 mb-3">
              <Hourglass className="w-4 h-4 text-[#b05a3f]" />
              <h2 className="font-serif text-lg">In attesa di recapito ({pending.length})</h2>
            </div>
            <div className="space-y-3">
              {pending.map((p) => (
                <div key={p.id} data-testid="pending-item" className="bg-[#FCF6F1] rounded-2xl border border-[#efd9cd] p-5 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0 border border-[#efd9cd]">
                    <Clock className="w-4 h-4 text-[#b05a3f]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs px-2.5 py-0.5 rounded-full bg-[#FBEDE7] text-[#b05a3f]">Dono a data</span>
                      <span className="inline-flex items-center gap-1 text-xs text-[#b05a3f]"><Hourglass className="w-3.5 h-3.5" /> In attesa</span>
                    </div>
                    <p className="font-medium text-[#2C2A29] break-words">«{p.title}»</p>
                    {p.recipients?.length > 0 && (
                      <p className="text-sm text-muted-foreground break-words">
                        A: {p.recipients.map((r) => r.name + (r.email ? ` · ${r.email}` : "")).join(", ")}
                      </p>
                    )}
                    <p className="text-xs text-[#b05a3f] mt-1">Previsto per: {fmtDateTz(p.release_date, p.release_tz)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {logs === null ? (
          <p className="text-muted-foreground">Caricamento…</p>
        ) : logs.length === 0 ? (
          <div
            className="text-center py-20 rounded-2xl border border-dashed border-border bg-white/40"
            data-testid="registro-empty"
          >
            <Inbox className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
            <p className="font-serif text-xl">Nessun invio ancora</p>
            <p className="text-muted-foreground mt-1 text-sm">
              Quando invierai un invito, un dono o altre notifiche, li ritroverai qui.
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-6" data-testid="registro-filters">
              <button
                onClick={() => setFilter("all")}
                data-testid="registro-filter-all"
                className={`px-4 py-1.5 rounded-full text-sm transition-colors ${
                  filter === "all" ? "bg-[#2C2A29] text-white" : "bg-[#F0E9DC] text-[#5a5148] hover:bg-[#e7ddca]"
                }`}
              >
                Tutte ({logs.length})
              </button>
              {usedCategories.map((c) => {
                const meta = CATEGORIES[c] || CATEGORIES.other;
                const count = logs.filter((l) => l.category === c).length;
                return (
                  <button
                    key={c}
                    onClick={() => setFilter(c)}
                    data-testid={`registro-filter-${c}`}
                    className={`px-4 py-1.5 rounded-full text-sm transition-colors ${
                      filter === c ? "bg-[#2C2A29] text-white" : "bg-[#F0E9DC] text-[#5a5148] hover:bg-[#e7ddca]"
                    }`}
                  >
                    {meta.label} ({count})
                  </button>
                );
              })}
            </div>

            <div className="space-y-3" data-testid="registro-list">
              {filtered.map((l) => {
                const cat = CATEGORIES[l.category] || CATEGORIES.other;
                const st = STATUS[l.status] || STATUS.sent;
                const StIcon = st.icon;
                return (
                  <div
                    key={l.id}
                    data-testid="registro-item"
                    className="bg-white rounded-2xl border border-border p-5 flex items-start gap-4 shadow-sm"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#FBEDE7] flex items-center justify-center shrink-0">
                      <Send className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-xs px-2.5 py-0.5 rounded-full ${cat.cls}`}>{cat.label}</span>
                        <span className={`inline-flex items-center gap-1 text-xs ${st.cls}`}>
                          <StIcon className="w-3.5 h-3.5" /> {st.label}
                        </span>
                        <span className="text-xs text-muted-foreground ml-auto">{fmtDate(l.created_at)}</span>
                      </div>
                      <p className="font-medium text-[#2C2A29] break-words">{l.subject}</p>
                      <p className="text-sm text-muted-foreground break-words">
                        A: {l.to_name ? `${l.to_name} · ` : ""}{l.to}
                      </p>
                      {l.status === "failed" && l.error && (
                        <p className="text-xs text-destructive mt-1 break-words line-clamp-2">Errore: {l.error}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default RegistroInvii;
